/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IIconNameItem, TSchemaConstObjectProps, TThemeStyleNames } from "@adapptio/lib-blueprint";
import { IResourceDescription, RESOURCE_TYPES } from "@adapptio/lib-core";
import { TSimpleEventEmitter } from "@adapptio/lib-shared";

export interface IEditorResourceDeleteEvent {
	deletedByLocalAction: boolean;
}

export interface IEditorResource_Base {
	id: string;
	descriptor: IResourceDescription;
	metaData: {
		lockedBy: string;
		lockedAt: Date;
		modifiedBy: string;
		modifiedAt: Date;
		revision: string;
	};
	onChange: TSimpleEventEmitter<void>;
	onDelete: TSimpleEventEmitter<IEditorResourceDeleteEvent>;
}

export interface IEditorResource_View extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.VIEW;
	paramsSchema: TSchemaConstObjectProps;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Action extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.ACTION;
	paramsSchema: TSchemaConstObjectProps;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Theme extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.THEME;
	styles: TThemeStyleNames;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Integration extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.INTEGRATION;
	integrationType: string;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Endpoint extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.ENDPOINT;
	integrationType: string;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Asset extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.ASSET;
	paramsSchema: TSchemaConstObjectProps;
	content: string;
	isContentUpToDate: boolean;
	metaData: IEditorResource_Base["metaData"] & {
		assetFilename?: string;
		assetMimetype?: string;
		assetSize?: number;
		assetUrl?: string;
	};
}

export interface IEditorResource_Routes extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.ROUTES;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Translations extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.TRANSLATIONS;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Readme extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.README;
	content: string;
	isContentUpToDate: boolean;
}

export interface IEditorResource_Directory extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.DIRECTORY;
}

export interface IEditorResource_Unknown extends IEditorResource_Base {
	resourceType: RESOURCE_TYPES.UNKNOWN;
}

export type TEditorResource =
	| IEditorResource_View
	| IEditorResource_Action
	| IEditorResource_Theme
	| IEditorResource_Asset
	| IEditorResource_Integration
	| IEditorResource_Endpoint
	| IEditorResource_Routes
	| IEditorResource_Translations
	| IEditorResource_Readme
	| IEditorResource_Directory
	| IEditorResource_Unknown;

export type TEditorResourceByType<TResourceType> = TResourceType extends RESOURCE_TYPES.VIEW
	? IEditorResource_View
	: TResourceType extends RESOURCE_TYPES.ACTION
	? IEditorResource_Action
	: TResourceType extends RESOURCE_TYPES.THEME
	? IEditorResource_Theme
	: TResourceType extends RESOURCE_TYPES.ASSET
	? IEditorResource_Asset
	: TResourceType extends RESOURCE_TYPES.INTEGRATION
	? IEditorResource_Integration
	: TResourceType extends RESOURCE_TYPES.ROUTES
	? IEditorResource_Routes
	: TResourceType extends RESOURCE_TYPES.TRANSLATIONS
	? IEditorResource_Translations
	: TResourceType extends RESOURCE_TYPES.README
	? IEditorResource_Readme
	: TResourceType extends RESOURCE_TYPES.DIRECTORY
	? IEditorResource_Directory
	: TResourceType extends RESOURCE_TYPES.ENDPOINT
	? IEditorResource_Endpoint
	: TResourceType extends RESOURCE_TYPES.UNKNOWN
	? IEditorResource_Unknown
	: never;

export type TEditorBlueprintResource =
	| IEditorResource_View
	| IEditorResource_Action
	| IEditorResource_Theme
	| IEditorResource_Asset
	| IEditorResource_Integration
	| IEditorResource_Endpoint
	| IEditorResource_Routes
	| IEditorResource_Translations
	| IEditorResource_Readme;

export type TBlueprintResourceTypes =
	| RESOURCE_TYPES.ACTION
	| RESOURCE_TYPES.THEME
	| RESOURCE_TYPES.ASSET
	| RESOURCE_TYPES.INTEGRATION
	| RESOURCE_TYPES.ENDPOINT
	| RESOURCE_TYPES.ROUTES
	| RESOURCE_TYPES.TRANSLATIONS
	| RESOURCE_TYPES.README
	| RESOURCE_TYPES.VIEW;

export const BLUEPRINT_RESOURCE_TYPES = [
	RESOURCE_TYPES.ACTION,
	RESOURCE_TYPES.ASSET,
	RESOURCE_TYPES.THEME,
	RESOURCE_TYPES.ENDPOINT,
	RESOURCE_TYPES.INTEGRATION,
	RESOURCE_TYPES.ROUTES,
	RESOURCE_TYPES.TRANSLATIONS,
	RESOURCE_TYPES.README,
	RESOURCE_TYPES.VIEW
];

export interface IResourceChanges {
	descriptorChanged: boolean;
	resourceChanged: boolean;
	typesChanged: Set<RESOURCE_TYPES>;
}

export interface IIconPack {
	name: string;
	label: string;
	shortLabel: string;
	description: string;
	icons: IIconNameItem[];
}
